import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Calendar, Clock, Trash2, Lock, Unlock, Plus, Loader2, Edit, X, FileStack, ArrowRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { API } from '@/utils/api';

const Admin = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [bookings, setBookings] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockWeekDialogOpen, setBlockWeekDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [manualBookingDialogOpen, setManualBookingDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [calendarStatus, setCalendarStatus] = useState(null);

  // Block slot form
  const [blockDate, setBlockDate] = useState('');
  const [blockTime, setBlockTime] = useState('');
  const [blockReason, setBlockReason] = useState('');

  // Block week form
  const [blockWeekStart, setBlockWeekStart] = useState('');
  const [blockWeekEnd, setBlockWeekEnd] = useState('');
  const [blockWeekReason, setBlockWeekReason] = useState('Holiday');

  // Edit booking form
  const [editStatus, setEditStatus] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');
  const [editNotes, setEditNotes] = useState('');

  // Manual booking form
  const [manualBooking, setManualBooking] = useState({
    service_id: 'domestic_epc',
    service_name: 'Domestic Property EPC',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    postcode: '',
    property_type: 'House',
    preferred_date: '',
    preferred_time: '',
    access_notes: '',
    payment_method: 'BACS',
    total_price: 0,
    travel_surcharge: 0
  });

  const AVAILABLE_TIMES = ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00'];

  // Restore admin session from sessionStorage (survives Google OAuth redirect).
  // Runs once on mount — sessionStorage / setUsername / setPassword are stable
  // browser/setState APIs, and lint sometimes flags them as "missing deps" but
  // adding them would cause an infinite loop (setState triggers re-run).
  useEffect(() => {
    const savedUser = sessionStorage.getItem('admin_user');
    const savedPass = sessionStorage.getItem('admin_pass');
    if (savedUser && savedPass) {
      setUsername(savedUser);
      setPassword(savedPass);
      setAuthenticated(true);
    }
  }, []);

  const createAuthHeader = useCallback(() => {
    const credentials = btoa(`${username}:${password}`);
    return { Authorization: `Basic ${credentials}` };
  }, [username, password]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const headers = createAuthHeader();
      await axios.get(`${API}/admin/bookings`, { headers });
      setAuthenticated(true);
      sessionStorage.setItem('admin_user', username);
      sessionStorage.setItem('admin_pass', password);
      toast.success('Login successful');
      fetchData();
    } catch (error) {
      toast.error('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const fetchData = useCallback(async () => {
    if (!authenticated) return;

    try {
      setLoading(true);
      const headers = createAuthHeader();

      const [bookingsRes, blockedRes, calendarRes] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { headers }),
        axios.get(`${API}/admin/blocked-slots`, { headers }),
        axios.get(`${API}/admin/calendar/status`, { headers }).catch(() => ({ data: null }))
      ]);

      setBookings(bookingsRes.data);
      setBlockedSlots(blockedRes.data);
      setCalendarStatus(calendarRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [authenticated, createAuthHeader]);

  useEffect(() => {
    if (authenticated) {
      fetchData();

      // Check for calendar connection success/failure from redirect
      const params = new URLSearchParams(window.location.search);
      if (params.get('calendar_connected') === 'true') {
        toast.success('Google Calendar connected successfully!');
        fetchData();
        window.history.replaceState({}, '', '/admin');
      } else if (params.get('calendar_connected') === 'false') {
        toast.error(params.get('error') === 'calendar_scope_missing'
          ? 'Calendar permission was not ticked on the Google screen. Click Connect again and tick "See, edit, share and delete all your Google Calendars".'
          : 'Failed to connect Google Calendar', { duration: 15000 });
        window.history.replaceState({}, '', '/admin');
      }
    }
  }, [authenticated, fetchData]);

  const handleBlockSlot = async () => {
    try {
      const headers = createAuthHeader();
      await axios.post(`${API}/admin/blocked-slots`, {
        date: blockDate,
        time: blockTime,
        reason: blockReason
      }, { headers });
      
      toast.success('Slot blocked successfully');
      setBlockDialogOpen(false);
      setBlockDate('');
      setBlockTime('');
      setBlockReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block slot');
    }
  };

  const handleUnblockSlot = async (slotId) => {
    try {
      const headers = createAuthHeader();
      await axios.delete(`${API}/admin/blocked-slots/${slotId}`, { headers });
      toast.success('Slot unblocked successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to unblock slot');
    }
  };

  const handleBlockWeek = async () => {
    if (!blockWeekStart || !blockWeekEnd) {
      toast.error('Please select both start and end dates');
      return;
    }
    try {
      const headers = createAuthHeader();
      const start = new Date(blockWeekStart);
      const end = new Date(blockWeekEnd);
      
      if (end < start) {
        toast.error('End date must be after start date');
        return;
      }

      let blockedCount = 0;
      const current = new Date(start);
      while (current <= end) {
        const dayOfWeek = current.getDay();
        // Only block Tuesdays (2) and Thursdays (4) - these are the available booking days
        if (dayOfWeek === 2 || dayOfWeek === 4) {
          const dateStr = current.toISOString().split('T')[0];
          for (const time of AVAILABLE_TIMES) {
            try {
              await axios.post(`${API}/admin/blocked-slots`, {
                date: dateStr,
                time: time,
                reason: blockWeekReason || 'Holiday'
              }, { headers });
              blockedCount++;
            } catch (err) {
              // Slot may already be blocked — that's fine, keep going. Only
              // log unexpected errors so blocking flow noise stays low.
              if (err?.response?.status !== 400 && err?.response?.status !== 409) {
                console.error('handleBlockWeek: unexpected slot-block error', err);
              }
            }
          }
        }
        current.setDate(current.getDate() + 1);
      }

      toast.success(`Blocked ${blockedCount} slots across selected dates`);
      setBlockWeekDialogOpen(false);
      setBlockWeekStart('');
      setBlockWeekEnd('');
      setBlockWeekReason('Holiday');
      fetchData();
    } catch (error) {
      toast.error('Failed to block dates');
    }
  };

  const handleConfirmBooking = async (bookingId) => {
    try {
      setLoading(true);
      const headers = createAuthHeader();
      await axios.post(`${API}/admin/bookings/${bookingId}/confirm`, {}, { headers });
      toast.success('Booking confirmed! Calendar event updated.');
      fetchData();
    } catch (error) {
      console.error('Error confirming booking:', error);
      toast.error('Failed to confirm booking');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    
    try {
      const headers = createAuthHeader();
      await axios.delete(`${API}/admin/bookings/${bookingId}`, { headers });
      toast.success('Booking deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete booking');
    }
  };

  const openEditDialog = (booking) => {
    setSelectedBooking(booking);
    setEditStatus(booking.status);
    setEditDate(booking.preferred_date);
    setEditTime(booking.preferred_time);
    setEditNotes(booking.access_notes || '');
    setEditDialogOpen(true);
  };

  const handleUpdateBooking = async () => {
    try {
      const headers = createAuthHeader();
      const updates = {};
      
      if (editStatus !== selectedBooking.status) updates.status = editStatus;
      if (editDate !== selectedBooking.preferred_date) updates.preferred_date = editDate;
      if (editTime !== selectedBooking.preferred_time) updates.preferred_time = editTime;
      if (editNotes !== selectedBooking.access_notes) updates.access_notes = editNotes;
      
      await axios.put(`${API}/admin/bookings/${selectedBooking.id}`, updates, { headers });
      
      toast.success('Booking updated successfully');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update booking');
    }
  };

  const handleCreateManualBooking = async () => {
    try {
      // Validation
      if (!manualBooking.customer_name || !manualBooking.customer_email || !manualBooking.customer_phone) {
        toast.error('Please fill in all customer details');
        return;
      }
      if (!manualBooking.postcode || !manualBooking.city) {
        toast.error('Please fill in location details (postcode and city are required)');
        return;
      }
      if (!manualBooking.preferred_date || !manualBooking.preferred_time) {
        toast.error('Please select date and time');
        return;
      }

      setLoading(true);
      const headers = createAuthHeader();
      await axios.post(`${API}/admin/bookings`, manualBooking, { headers });
      
      toast.success('Manual booking created successfully');
      setManualBookingDialogOpen(false);
      // Reset form
      setManualBooking({
        service_id: 'domestic_epc',
        service_name: 'Domestic Property EPC',
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        address_line1: '',
        address_line2: '',
        city: '',
        postcode: '',
        property_type: 'House',
        preferred_date: '',
        preferred_time: '',
        access_notes: '',
        payment_method: 'BACS',
        total_price: 0,
        travel_surcharge: 0
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatTime12hr = (time24) => {
    const [hour, minute] = time24.split(':').map(Number);
    const period = hour < 12 ? 'AM' : 'PM';
    const displayHour = hour <= 12 ? hour : hour - 12;
    return `${displayHour === 0 ? 12 : displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const handleConnectCalendar = async () => {
    try {
      const response = await axios.get(`${API}/auth/google/login`);
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error('Failed to initiate calendar connection');
    }
  };

  const handleSyncCalendar = async () => {
    try {
      const headers = createAuthHeader();
      const response = await axios.post(`${API}/admin/calendar/sync`, {}, { headers });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to sync calendar');
    }
  };

  const handleSendTestEmail = async () => {
    try {
      setLoading(true);
      const headers = createAuthHeader();
      const response = await axios.post(`${API}/admin/test-email`, {}, { headers });
      toast.success('Test email sent to web@energyperformancedirect.co.uk');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setLoading(false);
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Admin Login</CardTitle>
            <CardDescription>Enter your credentials to access the admin panel</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-green-600 to-cyan-500"
                disabled={loading}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Navbar />

      <section className="py-8 bg-gradient-to-r from-green-600 to-cyan-500 text-white">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold text-center">Admin Panel</h1>
          <p className="text-center mt-2 text-green-50">Manage bookings and appointment availability</p>
        </div>
      </section>

      <section className="py-12">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Evidence Pack Tool — internal (admin-only) shortcut */}
          <Link
            to="/portal/evidence"
            data-testid="admin-evidence-pack-shortcut"
            className="group block mb-8"
          >
            <div className="relative overflow-hidden rounded-xl border border-emerald-200 bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-900/40 text-slate-100 shadow-sm hover:shadow-lg transition-all">
              <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-emerald-500/10 blur-2xl group-hover:bg-emerald-500/20 transition-colors" aria-hidden="true" />
              <div className="relative flex items-start gap-4 p-5 md:p-6">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                  <FileStack className="w-6 h-6 text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest text-emerald-400 font-semibold">Internal tool</span>
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 border-l border-slate-700 pl-2">Commercial EPC</span>
                  </div>
                  <h3 className="text-lg md:text-xl font-semibold text-white mt-1">Evidence Pack Tool</h3>
                  <p className="text-sm text-slate-300 mt-1 leading-relaxed">
                    Upload bulk survey evidence (ZIP photos, notes) and Assessment Result PDFs (EPC / BRUKL / iSBEM),
                    reconcile ASSESSED_INPUT outcomes to real evidence, and generate the Evidence Pack.
                  </p>
                </div>
                <div className="hidden md:flex flex-col items-end justify-center flex-shrink-0 pl-3">
                  <span className="inline-flex items-center gap-1 text-emerald-300 group-hover:text-emerald-200 text-sm font-medium">
                    Open portal
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                  <span className="text-[10px] font-mono text-slate-500 mt-1">/portal/evidence</span>
                </div>
              </div>
            </div>
          </Link>

          <Tabs defaultValue="bookings" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="bookings">Bookings ({bookings.length})</TabsTrigger>
              <TabsTrigger value="blocked">Blocked Slots ({blockedSlots.length})</TabsTrigger>
              <TabsTrigger value="calendar">Google Calendar</TabsTrigger>
            </TabsList>

            <TabsContent value="bookings" className="space-y-4 mt-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">All Bookings</h2>
                <div className="flex gap-2">
                  <Dialog open={manualBookingDialogOpen} onOpenChange={setManualBookingDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-gradient-to-r from-green-600 to-cyan-500">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Manual Booking
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Create Manual Booking</DialogTitle>
                        <DialogDescription>
                          Create a booking manually (bypasses availability checks). This booking will block the slot for public bookings and will be used for travel-time calculations.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        {/* Service Selection */}
                        <div>
                          <Label htmlFor="manual-service">Service</Label>
                          <Select 
                            value={manualBooking.service_id}
                            onValueChange={(value) => {
                              const serviceNames = {
                                'domestic_epc': 'Domestic Property EPC',
                                'commercial_warehouse': 'Commercial EPC - Warehouse',
                                'commercial_hotel_office': 'Commercial EPC - Hotel/Office',
                                'sap_calculation': 'SAP Calculation',
                                'u_values': 'U-Value Calculations'
                              };
                              setManualBooking({...manualBooking, service_id: value, service_name: serviceNames[value] || value});
                            }}
                          >
                            <SelectTrigger id="manual-service">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="domestic_epc">Domestic Property EPC</SelectItem>
                              <SelectItem value="commercial_warehouse">Commercial EPC - Warehouse</SelectItem>
                              <SelectItem value="commercial_hotel_office">Commercial EPC - Hotel/Office</SelectItem>
                              <SelectItem value="sap_calculation">SAP Calculation</SelectItem>
                              <SelectItem value="u_values">U-Value Calculations</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Customer Details */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="manual-name">Customer Name *</Label>
                            <Input
                              id="manual-name"
                              value={manualBooking.customer_name}
                              onChange={(e) => setManualBooking({...manualBooking, customer_name: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label htmlFor="manual-phone">Phone *</Label>
                            <Input
                              id="manual-phone"
                              value={manualBooking.customer_phone}
                              onChange={(e) => setManualBooking({...manualBooking, customer_phone: e.target.value})}
                            />
                          </div>
                        </div>

                        <div>
                          <Label htmlFor="manual-email">Email *</Label>
                          <Input
                            id="manual-email"
                            type="email"
                            value={manualBooking.customer_email}
                            onChange={(e) => setManualBooking({...manualBooking, customer_email: e.target.value})}
                          />
                        </div>

                        {/* Location Details */}
                        <div className="border-t pt-4">
                          <h4 className="font-semibold mb-3">Property Location (Required for Travel Calculations)</h4>
                          <div className="space-y-3">
                            <div>
                              <Label htmlFor="manual-address1">Address Line 1</Label>
                              <Input
                                id="manual-address1"
                                value={manualBooking.address_line1}
                                onChange={(e) => setManualBooking({...manualBooking, address_line1: e.target.value})}
                              />
                            </div>
                            <div>
                              <Label htmlFor="manual-address2">Address Line 2</Label>
                              <Input
                                id="manual-address2"
                                value={manualBooking.address_line2}
                                onChange={(e) => setManualBooking({...manualBooking, address_line2: e.target.value})}
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="manual-city">Town/City *</Label>
                                <Input
                                  id="manual-city"
                                  value={manualBooking.city}
                                  onChange={(e) => setManualBooking({...manualBooking, city: e.target.value})}
                                />
                              </div>
                              <div>
                                <Label htmlFor="manual-postcode">Postcode * (Required)</Label>
                                <Input
                                  id="manual-postcode"
                                  value={manualBooking.postcode}
                                  onChange={(e) => setManualBooking({...manualBooking, postcode: e.target.value.toUpperCase()})}
                                  placeholder="EX1 1AA"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Date & Time */}
                        <div className="border-t pt-4">
                          <h4 className="font-semibold mb-3">Appointment Details</h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="manual-date">Date *</Label>
                              <Input
                                id="manual-date"
                                type="date"
                                min={getMinDate()}
                                value={manualBooking.preferred_date}
                                onChange={(e) => setManualBooking({...manualBooking, preferred_date: e.target.value})}
                              />
                            </div>
                            <div>
                              <Label htmlFor="manual-time">Time *</Label>
                              <Select 
                                value={manualBooking.preferred_time}
                                onValueChange={(value) => setManualBooking({...manualBooking, preferred_time: value})}
                              >
                                <SelectTrigger id="manual-time">
                                  <SelectValue placeholder="Select time" />
                                </SelectTrigger>
                                <SelectContent>
                                  {AVAILABLE_TIMES.map(time => (
                                    <SelectItem key={time} value={time}>{time}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>

                        {/* Additional Info */}
                        <div>
                          <Label htmlFor="manual-notes">Access Notes</Label>
                          <Textarea
                            id="manual-notes"
                            value={manualBooking.access_notes}
                            onChange={(e) => setManualBooking({...manualBooking, access_notes: e.target.value})}
                            placeholder="e.g., Key location, parking instructions, special access requirements"
                          />
                        </div>

                        {/* Payment */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="manual-price">Total Price (£)</Label>
                            <Input
                              id="manual-price"
                              type="number"
                              step="0.01"
                              value={manualBooking.total_price}
                              onChange={(e) => setManualBooking({...manualBooking, total_price: parseFloat(e.target.value) || 0})}
                            />
                          </div>
                          <div>
                            <Label htmlFor="manual-payment">Payment Method</Label>
                            <Select 
                              value={manualBooking.payment_method}
                              onValueChange={(value) => setManualBooking({...manualBooking, payment_method: value})}
                            >
                              <SelectTrigger id="manual-payment">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="BACS">BACS</SelectItem>
                                <SelectItem value="Cash">Cash</SelectItem>
                                <SelectItem value="Cheque">Cheque</SelectItem>
                                <SelectItem value="PayPal">PayPal</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="flex justify-end gap-2 pt-4">
                          <Button variant="outline" onClick={() => setManualBookingDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button 
                            className="bg-gradient-to-r from-green-600 to-cyan-500"
                            onClick={handleCreateManualBooking}
                            disabled={loading}
                          >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            Create Booking
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Button onClick={fetchData} variant="outline">
                    Refresh
                  </Button>
                </div>
              </div>

              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-green-600" />
                </div>
              ) : bookings.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center text-gray-500">
                    No bookings found
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {bookings.map(booking => (
                    <Card key={booking.id}>
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-lg">{booking.customer_name}</CardTitle>
                            <CardDescription>
                              Ref: {booking.booking_reference} • {booking.service_name}
                            </CardDescription>
                          </div>
                          <div className="flex gap-2">
                            {booking.status === 'to_be_confirmed' && (
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => handleConfirmBooking(booking.id)}
                                disabled={loading}
                              >
                                ✓ Confirm
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditDialog(booking)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteBooking(booking.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">Date & Time</p>
                            <p className="font-medium">
                              {formatDate(booking.preferred_date)}
                              <br />
                              {formatTime12hr(booking.preferred_time)}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Contact</p>
                            <p className="font-medium">{booking.customer_email}</p>
                            <p className="text-xs">{booking.customer_phone}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Location</p>
                            <p className="font-medium">{booking.postcode}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Status</p>
                            <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                              booking.status === 'to_be_confirmed' ? 'bg-green-100 text-green-800' :
                              booking.status === 'confirmed' ? 'bg-yellow-100 text-yellow-800' :
                              booking.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                              booking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {booking.status === 'to_be_confirmed' ? '⚠️ TO BE CONFIRMED' : booking.status.toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="mt-4 pt-4 border-t">
                          <p className="text-sm font-medium">Total: £{booking.total_price.toFixed(2)}</p>
                          {booking.access_notes && (
                            <p className="text-sm text-gray-600 mt-2">
                              <strong>Notes:</strong> {booking.access_notes}
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="blocked" className="space-y-4 mt-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Blocked Time Slots</h2>
                <div className="flex gap-2">
                  <Dialog open={blockWeekDialogOpen} onOpenChange={setBlockWeekDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid="block-week-btn">
                        <Calendar className="w-4 h-4 mr-2" />
                        Block Holiday / Week
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Block Date Range (Holiday)</DialogTitle>
                        <DialogDescription>
                          Block all available booking slots between two dates. Only Tuesdays and Thursdays will be affected.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label htmlFor="block-week-start">Start Date</Label>
                          <Input
                            id="block-week-start"
                            type="date"
                            min={getMinDate()}
                            value={blockWeekStart}
                            onChange={(e) => setBlockWeekStart(e.target.value)}
                          />
                        </div>
                        <div>
                          <Label htmlFor="block-week-end">End Date</Label>
                          <Input
                            id="block-week-end"
                            type="date"
                            min={blockWeekStart || getMinDate()}
                            value={blockWeekEnd}
                            onChange={(e) => setBlockWeekEnd(e.target.value)}
                          />
                        </div>
                        <div>
                          <Label htmlFor="block-week-reason">Reason</Label>
                          <Input
                            id="block-week-reason"
                            value={blockWeekReason}
                            onChange={(e) => setBlockWeekReason(e.target.value)}
                            placeholder="e.g., Holiday, Annual leave"
                          />
                        </div>
                        <Button 
                          onClick={handleBlockWeek} 
                          className="w-full bg-red-600 hover:bg-red-700"
                          disabled={!blockWeekStart || !blockWeekEnd}
                          data-testid="confirm-block-week-btn"
                        >
                          Block All Slots in Range
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-gradient-to-r from-green-600 to-cyan-500">
                      <Lock className="w-4 h-4 mr-2" />
                      Block Slot
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Block Time Slot</DialogTitle>
                      <DialogDescription>
                        Prevent customers from booking a specific date and time
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="block-date">Date</Label>
                        <Input
                          id="block-date"
                          type="date"
                          min={getMinDate()}
                          value={blockDate}
                          onChange={(e) => setBlockDate(e.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="block-time">Time</Label>
                        <Select value={blockTime} onValueChange={setBlockTime}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select time" />
                          </SelectTrigger>
                          <SelectContent>
                            {AVAILABLE_TIMES.map(time => (
                              <SelectItem key={time} value={time}>
                                {formatTime12hr(time)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="block-reason">Reason (optional)</Label>
                        <Textarea
                          id="block-reason"
                          value={blockReason}
                          onChange={(e) => setBlockReason(e.target.value)}
                          placeholder="e.g., Personal appointment, Holiday, etc."
                        />
                      </div>
                      <Button 
                        onClick={handleBlockSlot} 
                        className="w-full"
                        disabled={!blockDate || !blockTime}
                      >
                        Block Slot
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
                </div>
              </div>

              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-green-600" />
                </div>
              ) : blockedSlots.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center text-gray-500">
                    No blocked slots
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {blockedSlots.map(slot => (
                    <Card key={slot.id}>
                      <CardContent className="flex justify-between items-center py-4">
                        <div className="flex items-center gap-4">
                          <Lock className="w-5 h-5 text-red-500" />
                          <div>
                            <p className="font-medium">
                              {formatDate(slot.date)} at {formatTime12hr(slot.time)}
                            </p>
                            {slot.reason && (
                              <p className="text-sm text-gray-500">{slot.reason}</p>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUnblockSlot(slot.id)}
                        >
                          <Unlock className="w-4 h-4 mr-2" />
                          Unblock
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="calendar" className="space-y-4 mt-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Google Calendar Integration</h2>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Connection Status</CardTitle>
                  <CardDescription>Manage your Google Calendar integration</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {calendarStatus && (
                    <div>
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`w-3 h-3 rounded-full ${calendarStatus.authenticated ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <span className="font-medium">
                          {calendarStatus.authenticated ? 'Connected' : 'Not Connected'}
                        </span>
                      </div>
                      
                      {calendarStatus.authenticated ? (
                        <div className="space-y-3">
                          <p className="text-sm text-gray-600">
                            <strong>Email:</strong> {calendarStatus.email}
                          </p>
                          <p className="text-sm text-gray-600">
                            Bookings will automatically sync to your Google Calendar.
                          </p>
                          <div className="flex gap-2">
                            <Button onClick={handleSyncCalendar} variant="outline">
                              Sync Now
                            </Button>
                            <Button onClick={handleConnectCalendar} variant="outline">
                              Reconnect
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-sm text-gray-600">
                            {calendarStatus.message || 'Connect your Google Calendar to automatically sync bookings.'}
                          </p>
                          <Button 
                            onClick={handleConnectCalendar}
                            className="bg-gradient-to-r from-green-600 to-cyan-500"
                          >
                            <Calendar className="w-4 h-4 mr-2" />
                            Connect Google Calendar
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>How It Works</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• When a customer books an appointment, it's automatically added to your Google Calendar</li>
                    <li>• Calendar events include customer details, service type, and location</li>
                    <li>• When you manually create a "Busy" event in Google Calendar, that time slot becomes unavailable for booking</li>
                    <li>• Deleting a booking in the admin panel also removes it from your Google Calendar</li>
                    <li>• All times are in UK timezone (Europe/London)</li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Manual Blocking from Calendar</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-3">
                    To block a time slot from your Google Calendar:
                  </p>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
                    <li>Open Google Calendar (calendar.google.com)</li>
                    <li>Create a new event for the time you want to block</li>
                    <li>Set the event as "Busy" (default) or include "Busy" in the title</li>
                    <li>The booking system will automatically recognize this and mark the slot as unavailable</li>
                  </ol>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Email Notifications</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-3">
                    All booking notifications are sent to: <strong>web@energyperformancedirect.co.uk</strong>
                  </p>
                  <Button 
                    onClick={handleSendTestEmail} 
                    variant="outline"
                    disabled={loading}
                  >
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : '📧'}
                    Send Test Email
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Edit Booking Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Booking</DialogTitle>
            <DialogDescription>
              Update booking details
            </DialogDescription>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4 mt-4">
              <div>
                <Label htmlFor="edit-status">Status</Label>
                <Select value={editStatus} onValueChange={setEditStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-date">Date</Label>
                <Input
                  id="edit-date"
                  type="date"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="edit-time">Time</Label>
                <Select value={editTime} onValueChange={setEditTime}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABLE_TIMES.map(time => (
                      <SelectItem key={time} value={time}>
                        {formatTime12hr(time)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-notes">Access Notes</Label>
                <Textarea
                  id="edit-notes"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                />
              </div>
              <Button onClick={handleUpdateBooking} className="w-full">
                Update Booking
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default Admin;
